import React from "react";

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";


/*
===========================================
STYLE SHEET
===========================================
*/

const styles = StyleSheet.create({

  page: {
    padding: 35,
    backgroundColor: "#f5f5f5",
    fontSize: 10,
    color: "#222222",
  },


  header: {
    backgroundColor: "#5b3700",
    padding: 20,
    borderRadius: 6,
    marginBottom: 18,
    breakInside: "avoid",
  },


  schoolName: {
    fontSize: 20,
    color: "#ffffff",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 5,
  },


  reportTitle: {
    fontSize: 15,
    color: "#ffffff",
    textAlign: "center",
    marginBottom: 4,
  },


  reportSubtitle: {
    fontSize: 10,
    color: "#eeeeee",
    textAlign: "center",
  },


  section: {
    backgroundColor: "#ffffff",
    borderRadius: 6,
    padding: 15,
    marginBottom: 15,
    breakInside: "avoid",
  },


  tableSection: {
    backgroundColor: "#ffffff",
    borderRadius: 6,
    padding: 15,
    marginBottom: 15,
  },


  sectionTitle: {
    fontSize: 13,
    color: "#ff8c00",
    fontWeight: "bold",
    marginBottom: 10,
  },


  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#eeeeee",
    paddingBottom: 4,
  },


  summaryLabel: {
    fontWeight: "bold",
    color: "#555555",
  },


  summaryValue: {
    color: "#222222",
  },


  podiumContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
    breakInside: "avoid",
  },


  podiumCard: {
    width: "31%",
    borderWidth: 1,
    borderColor: "#dddddd",
    borderRadius: 5,
    padding: 10,
    textAlign: "center",
    breakInside: "avoid",
  },


  podiumName: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 5,
  },


  podiumAverage: {
    fontSize: 10,
    color: "#800000",
  },


  /*
  ===============================
  TABLE STYLES
  ===============================
  */


  table: {
    display: "table",
    width: "100%",
    borderWidth: 1,
    borderColor: "#cccccc",
  },


  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#4b0000",
    repeat: true,
  },


  tableRow: {
    flexDirection: "row",
    breakInside: "avoid",
  },


  cell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#cccccc",
    padding: 6,
    fontSize: 9,
  },


  rankCell: {
    width: "10%",
    textAlign: "center",
  },


  studentCell: {
    width: "35%",
  },


  classCell: {
    width: "20%",
  },


  averageCell: {
    width: "17%",
    textAlign: "center",
  },


  gradeCell: {
    width: "18%",
    textAlign: "center",
  },


  signatureSection: {
    marginTop: 35,
    flexDirection: "row",
    justifyContent: "space-between",
    breakInside: "avoid",
  },


  signatureBox: {
    width: "42%",
    alignItems: "center",
  },


  signatureLine: {
    borderTopWidth: 1,
    borderTopColor: "#000000",
    width: "100%",
    marginBottom: 5,
  },


  signatureText: {
    fontSize: 9,
  },


  footer: {
    marginTop: 25,
    textAlign: "center",
    fontSize: 8,
    color: "#777777",
  },

});



/*
===========================================
HELPER COMPONENTS
===========================================
*/


const SummaryItem = ({
  label,
  value,
}) => (
  <View style={styles.summaryRow}>

    <Text style={styles.summaryLabel}>
      {label}
    </Text>

    <Text style={styles.summaryValue}>
      {value}
    </Text>

  </View>
);



const PodiumStudent = ({
  student,
}) => (

  <View style={styles.podiumCard}>

    <Text style={styles.podiumName}>
      {student?.student_name || "--"}
    </Text>


    <Text style={styles.podiumAverage}>
      {student?.average_score || "--"}%
    </Text>

  </View>

);



/*
===========================================
MAIN COMPONENT
===========================================
*/


const TopStudentPerPeriodPDF = ({
  data,
}) => {


  if (!data) {
    return null;
  }


  const students = data.students || [];


  const dux =
    students.length > 0
      ? students[0]
      : null;


  const secondPlace =
    students.length > 1
      ? students[1]
      : null;


  const thirdPlace =
    students.length > 2
      ? students[2]
      : null;



  return (

    <Document>


      <Page
        size="A4"
        style={styles.page}
        wrap
      >


        <View style={styles.header}>


          <Text style={styles.schoolName}>
            Jana L. Williams Educational Foundation
          </Text>


          <Text style={styles.reportTitle}>
            TOP STUDENTS PER PERIOD REPORT
          </Text>


          <Text style={styles.reportSubtitle}>
            Academic Excellence & Student Ranking Report
          </Text>


        </View>




        <View style={styles.section}>


          <Text style={styles.sectionTitle}>
            Report Information
          </Text>



          <SummaryItem
            label="Academic Year"
            value={data.academic_year}
          />


          <SummaryItem
            label="Grade Class"
            value={data.grade_class}
          />


          <SummaryItem
            label="Academic Period"
            value={data.period}
          />


          <SummaryItem
            label="Total Ranked Students"
            value={students.length}
          />


          <SummaryItem
            label="Report Generated"
            value={
              new Date().toLocaleDateString()
            }
          />


        </View>




        <View style={styles.section}>


          <Text style={styles.sectionTitle}>
            Class Dux
          </Text>



          {
            dux ? (

              <>

                <SummaryItem
                  label="Student Name"
                  value={dux.student_name}
                />


                <SummaryItem
                  label="Rank"
                  value={`#${dux.rank}`}
                />


                <SummaryItem
                  label="Average Score"
                  value={`${dux.average_score}%`}
                />


                <SummaryItem
                  label="Grade"
                  value={dux.alphabet_grade}
                />


              </>


            ) : (

              <Text>
                No ranking data available.
              </Text>

            )
          }



        </View>





        <View style={styles.section}>


          <Text style={styles.sectionTitle}>
            Top Three Students
          </Text>



          <View style={styles.podiumContainer}>


            <PodiumStudent
              student={dux}
            />


            <PodiumStudent
              student={secondPlace}
            />


            <PodiumStudent
              student={thirdPlace}
            />


          </View>



        </View>
        <View
          style={styles.tableSection}
          minPresenceAhead={120}
        >

          <Text style={styles.sectionTitle}>
            Student Ranking
          </Text>



          <View style={styles.table}>


            <View
              style={styles.tableHeader}
              fixed
            >

              <Text
                style={[
                  styles.cell,
                  styles.rankCell,
                ]}
              >
                Rank
              </Text>



              <Text
                style={[
                  styles.cell,
                  styles.studentCell,
                ]}
              >
                Student Name
              </Text>



              <Text
                style={[
                  styles.cell,
                  styles.classCell,
                ]}
              >
                Grade Class
              </Text>



              <Text
                style={[
                  styles.cell,
                  styles.averageCell,
                ]}
              >
                Average
              </Text>



              <Text
                style={[
                  styles.cell,
                  styles.gradeCell,
                ]}
              >
                Grade
              </Text>


            </View>





            {
              students.map(
                (
                  student,
                  index
                ) => (

                  <View
                    key={index}
                    style={styles.tableRow}
                    wrap={false}
                  >


                    <Text
                      style={[
                        styles.cell,
                        styles.rankCell,
                      ]}
                    >
                      {student.rank}
                    </Text>




                    <Text
                      style={[
                        styles.cell,
                        styles.studentCell,
                      ]}
                    >
                      {student.student_name}
                    </Text>




                    <Text
                      style={[
                        styles.cell,
                        styles.classCell,
                      ]}
                    >
                      {student.grade_class}
                    </Text>




                    <Text
                      style={[
                        styles.cell,
                        styles.averageCell,
                      ]}
                    >
                      {student.average_score}%
                    </Text>




                    <Text
                      style={[
                        styles.cell,
                        styles.gradeCell,
                      ]}
                    >
                      {student.alphabet_grade}
                    </Text>



                  </View>


                )

              )
            }



          </View>



        </View>







        <View
          style={styles.signatureSection}
        >



          <View
            style={styles.signatureBox}
          >

            <View
              style={styles.signatureLine}
            />


            <Text
              style={styles.signatureText}
            >
              Class Teacher
            </Text>


          </View>





          <View
            style={styles.signatureBox}
          >

            <View
              style={styles.signatureLine}
            />


            <Text
              style={styles.signatureText}
            >
              Academic Director
            </Text>


          </View>



        </View>







        <Text
          style={styles.footer}
        >

          This report is confidential and intended for official school use only.

        </Text>





        <Text
          style={styles.footer}
        >

          Generated by the School Management System

        </Text>





      </Page>
      <Page
        size="A4"
        style={styles.page}
        wrap
      >


        <View style={styles.header}>


          <Text style={styles.schoolName}>
            FRONTLINE WORD FAMILY SCHOOL
          </Text>


          <Text style={styles.reportTitle}>
            COMPLETE STUDENT RANKING
          </Text>


          <Text style={styles.reportSubtitle}>
            {data.grade_class}
            {" • "}
            {data.period}
            {" • "}
            {data.academic_year}
          </Text>


        </View>







        <View
          style={styles.tableSection}
          minPresenceAhead={150}
        >


          <Text style={styles.sectionTitle}>
            Full Ranking Register
          </Text>





          <View style={styles.table}>


            <View
              style={styles.tableHeader}
              fixed
            >


              <Text
                style={[
                  styles.cell,
                  styles.rankCell,
                ]}
              >
                Rank
              </Text>




              <Text
                style={[
                  styles.cell,
                  styles.studentCell,
                ]}
              >
                Student Name
              </Text>




              <Text
                style={[
                  styles.cell,
                  styles.classCell,
                ]}
              >
                Grade Class
              </Text>




              <Text
                style={[
                  styles.cell,
                  styles.averageCell,
                ]}
              >
                Average
              </Text>




              <Text
                style={[
                  styles.cell,
                  styles.gradeCell,
                ]}
              >
                Grade
              </Text>



            </View>







            {
              students.map(
                (
                  student,
                  index
                ) => (


                  <View
                    key={index}
                    style={styles.tableRow}
                    wrap={false}
                  >


                    <Text
                      style={[
                        styles.cell,
                        styles.rankCell,
                      ]}
                    >
                      {student.rank}
                    </Text>





                    <Text
                      style={[
                        styles.cell,
                        styles.studentCell,
                      ]}
                    >
                      {student.student_name}
                    </Text>





                    <Text
                      style={[
                        styles.cell,
                        styles.classCell,
                      ]}
                    >
                      {student.grade_class}
                    </Text>





                    <Text
                      style={[
                        styles.cell,
                        styles.averageCell,
                      ]}
                    >
                      {student.average_score}%
                    </Text>





                    <Text
                      style={[
                        styles.cell,
                        styles.gradeCell,
                      ]}
                    >
                      {student.alphabet_grade}
                    </Text>




                  </View>


                )
              )
            }




          </View>



        </View>









        <View style={styles.section}>


          <Text style={styles.sectionTitle}>
            Report Summary
          </Text>




          <SummaryItem
            label="Total Ranked Students"
            value={students.length}
          />



          <SummaryItem
            label="Highest Average"
            value={
              dux
                ? `${dux.average_score}%`
                : "--"
            }
          />



          <SummaryItem
            label="Class Dux"
            value={
              dux?.student_name || "--"
            }
          />



          <SummaryItem
            label="Academic Period"
            value={data.period}
          />



          <SummaryItem
            label="Academic Year"
            value={data.academic_year}
          />



        </View>









        <View
          style={styles.signatureSection}
        >



          <View
            style={styles.signatureBox}
          >


            <View
              style={styles.signatureLine}
            />



            <Text
              style={styles.signatureText}
            >
              Principal
            </Text>



          </View>







          <View
            style={styles.signatureBox}
          >


            <View
              style={styles.signatureLine}
            />



            <Text
              style={styles.signatureText}
            >
              School Stamp
            </Text>



          </View>



        </View>









        <Text
          style={styles.footer}
        >

          © Jana L. Williams Educational Foundation • Student Ranking Report

        </Text>






        <Text
          style={styles.footer}
        >

          Generated by the School Management System

        </Text>





      </Page>



    </Document>

  );

};



export default TopStudentPerPeriodPDF;


// import React from "react";

// import {
//   Document,
//   Page,
//   Text,
//   View,
//   StyleSheet,
// } from "@react-pdf/renderer";

// const styles = StyleSheet.create({
//   page: {
//     padding: 35,
//     backgroundColor: "#f5f5f5",
//     fontSize: 10,
//     color: "#222222",
//   },

//   header: {
//     backgroundColor: "#5b3700",
//     padding: 20,
//     borderRadius: 6,
//     marginBottom: 18,
//   },

//   schoolName: {
//     fontSize: 20,
//     color: "#ffffff",
//     fontWeight: "bold",
//     textAlign: "center",
//     marginBottom: 5,
//   },

//   reportTitle: {
//     fontSize: 15,
//     color: "#ffffff",
//     textAlign: "center",
//     marginBottom: 4,
//   },

//   reportSubtitle: {
//     fontSize: 10,
//     color: "#eeeeee",
//     textAlign: "center",
//   },

//   section: {
//     backgroundColor: "#ffffff",
//     borderRadius: 6,
//     padding: 15,
//     marginBottom: 15,
//   },

//   sectionTitle: {
//     fontSize: 13,
//     color: "#ff8c00",
//     fontWeight: "bold",
//     marginBottom: 10,
//   },

//   summaryRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginBottom: 6,
//     borderBottomWidth: 1,
//     borderBottomColor: "#eeeeee",
//     paddingBottom: 4,
//   },

//   summaryLabel: {
//     fontWeight: "bold",
//     color: "#555555",
//   },

//   summaryValue: {
//     color: "#222222",
//   },

//   podiumContainer: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginTop: 10,
//   },

//   podiumCard: {
//     width: "31%",
//     borderWidth: 1,
//     borderColor: "#dddddd",
//     borderRadius: 5,
//     padding: 10,
//     textAlign: "center",
//   },

//   podiumRank: {
//     fontSize: 18,
//     marginBottom: 6,
//   },

//   podiumName: {
//     fontSize: 11,
//     fontWeight: "bold",
//     marginBottom: 5,
//   },

//   podiumAverage: {
//     fontSize: 10,
//     color: "#800000",
//   },

//   table: {
//     display: "table",
//     width: "100%",
//     borderWidth: 1,
//     borderColor: "#cccccc",
//   },

//   tableHeader: {
//     flexDirection: "row",
//     backgroundColor: "#4b0000",
//   },

//   tableRow: {
//     flexDirection: "row",
//   },

//   cell: {
//     borderRightWidth: 1,
//     borderBottomWidth: 1,
//     borderColor: "#cccccc",
//     padding: 6,
//     fontSize: 9,
//   },

//   rankCell: {
//     width: "10%",
//     textAlign: "center",
//   },

//   studentCell: {
//     width: "35%",
//   },

//   classCell: {
//     width: "20%",
//   },

//   averageCell: {
//     width: "17%",
//     textAlign: "center",
//   },

//   gradeCell: {
//     width: "18%",
//     textAlign: "center",
//   },

//   footer: {
//     marginTop: 25,
//     textAlign: "center",
//     fontSize: 8,
//     color: "#777777",
//   },

//   signatureSection: {
//     marginTop: 35,
//     flexDirection: "row",
//     justifyContent: "space-between",
//   },

//   signatureBox: {
//     width: "42%",
//     alignItems: "center",
//   },

//   signatureLine: {
//     borderTopWidth: 1,
//     borderTopColor: "#000000",
//     width: "100%",
//     marginBottom: 5,
//   },

//   signatureText: {
//     fontSize: 9,
//   },
// });

// const TopStudentPerPeriodPDF = ({ data }) => {
//   if (!data) {
//     return null;
//   }

//   const students = data.students || [];

//   const dux = students.length > 0 ? students[0] : null;

//   const secondPlace = students.length > 1 ? students[1] : null;

//   const thirdPlace = students.length > 2 ? students[2] : null;
//   return (
//     <Document>
//       <Page
//         size="A4"
//         style={styles.page}
//       >
//         <View style={styles.header}>
//           <Text style={styles.schoolName}>
//             Jana L. Williams Educational Foundation School System
//           </Text>

//           <Text style={styles.reportTitle}>
//             TOP STUDENTS PER PERIOD REPORT
//           </Text>

//           <Text style={styles.reportSubtitle}>
//             Academic Excellence & Student Ranking Report
//           </Text>
//         </View>

//         <View style={styles.section}>
//           <Text style={styles.sectionTitle}>
//             Report Information
//           </Text>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Academic Year
//             </Text>

//             <Text style={styles.summaryValue}>
//               {data.academic_year}
//             </Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Grade Class
//             </Text>

//             <Text style={styles.summaryValue}>
//               {data.grade_class}
//             </Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Academic Period
//             </Text>

//             <Text style={styles.summaryValue}>
//               {data.period}
//             </Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Total Ranked Students
//             </Text>

//             <Text style={styles.summaryValue}>
//               {students.length}
//             </Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Report Generated
//             </Text>

//             <Text style={styles.summaryValue}>
//               {new Date().toLocaleDateString()}
//             </Text>
//           </View>
//         </View>

//         <View style={styles.section}>
//           <Text style={styles.sectionTitle}>
//             Class Dux
//           </Text>

//           {dux ? (
//             <>
//               <View style={styles.summaryRow}>
//                 <Text style={styles.summaryLabel}>
//                   Student Name
//                 </Text>

//                 <Text style={styles.summaryValue}>
//                   {dux.student_name}
//                 </Text>
//               </View>

//               <View style={styles.summaryRow}>
//                 <Text style={styles.summaryLabel}>
//                   Rank
//                 </Text>

//                 <Text style={styles.summaryValue}>
//                   #{dux.rank}
//                 </Text>
//               </View>

//               <View style={styles.summaryRow}>
//                 <Text style={styles.summaryLabel}>
//                   Average Score
//                 </Text>

//                 <Text style={styles.summaryValue}>
//                   {dux.average_score}%
//                 </Text>
//               </View>

//               <View style={styles.summaryRow}>
//                 <Text style={styles.summaryLabel}>
//                   Grade
//                 </Text>

//                 <Text style={styles.summaryValue}>
//                   {dux.alphabet_grade}
//                 </Text>
//               </View>
//             </>
//           ) : (
//             <Text>
//               No ranking data available.
//             </Text>
//           )}
//         </View>

//         <View style={styles.section}>
//           <Text style={styles.sectionTitle}>
//             Top Three Students
//           </Text>

//           <View style={styles.podiumContainer}>
//             <View style={styles.podiumCard}>
          

//               <Text style={styles.podiumName}>
//                 {dux?.student_name || "--"}
//               </Text>

//               <Text style={styles.podiumAverage}>
//                 {dux?.average_score || "--"}%
//               </Text>
//             </View>

//             <View style={styles.podiumCard}>


//               <Text style={styles.podiumName}>
//                 {secondPlace?.student_name || "--"}
//               </Text>

//               <Text style={styles.podiumAverage}>
//                 {secondPlace?.average_score || "--"}%
//               </Text>
//             </View>

//             <View style={styles.podiumCard}>
 

//               <Text style={styles.podiumName}>
//                 {thirdPlace?.student_name || "--"}
//               </Text>

//               <Text style={styles.podiumAverage}>
//                 {thirdPlace?.average_score || "--"}%
//               </Text>
//             </View>
//           </View>
//         </View>

//         <View style={styles.section}>
//           <Text style={styles.sectionTitle}>
//             Student Ranking
//           </Text>

//           <View style={styles.table}>
//             <View style={styles.tableHeader}>
//               <Text
//                 style={[
//                   styles.cell,
//                   styles.rankCell,
//                 ]}
//               >
//                 Rank
//               </Text>

//               <Text
//                 style={[
//                   styles.cell,
//                   styles.studentCell,
//                 ]}
//               >
//                 Student Name
//               </Text>

//               <Text
//                 style={[
//                   styles.cell,
//                   styles.classCell,
//                 ]}
//               >
//                 Grade Class
//               </Text>

//               <Text
//                 style={[
//                   styles.cell,
//                   styles.averageCell,
//                 ]}
//               >
//                 Average
//               </Text>

//               <Text
//                 style={[
//                   styles.cell,
//                   styles.gradeCell,
//                 ]}
//               >
//                 Grade
//               </Text>
//             </View>

//             {students.map((student, index) => (
//               <View
//                 key={index}
//                 style={styles.tableRow}
//               >
//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.rankCell,
//                   ]}
//                 >
//                   {student.rank}
//                 </Text>

//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.studentCell,
//                   ]}
//                 >
//                   {student.student_name}
//                 </Text>

//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.classCell,
//                   ]}
//                 >
//                   {student.grade_class}
//                 </Text>

//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.averageCell,
//                   ]}
//                 >
//                   {student.average_score}%
//                 </Text>

//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.gradeCell,
//                   ]}
//                 >
//                   {student.alphabet_grade}
//                 </Text>
//               </View>
//             ))}
//           </View>
//         </View>

//         <View style={styles.signatureSection}>
//           <View style={styles.signatureBox}>
//             <View style={styles.signatureLine} />

//             <Text style={styles.signatureText}>
//               Class Teacher
//             </Text>
//           </View>

//           <View style={styles.signatureBox}>
//             <View style={styles.signatureLine} />

//             <Text style={styles.signatureText}>
//               Academic Director
//             </Text>
//           </View>
//         </View>

//         <Text style={styles.footer}>
//           This report is confidential and intended for official school use only.
//         </Text>

//         <Text style={styles.footer}>
//           Generated by the School Management System
//         </Text>
//       </Page>

//       <Page
//         size="A4"
//         style={styles.page}
//       >
//         <View style={styles.header}>
//           <Text style={styles.schoolName}>
//             FRONTLINE WORD FAMILY SCHOOL
//           </Text>

//           <Text style={styles.reportTitle}>
//             COMPLETE STUDENT RANKING
//           </Text>

//           <Text style={styles.reportSubtitle}>
//             {data.grade_class} • {data.period} • {data.academic_year}
//           </Text>
//         </View>

//         <View style={styles.section}>
//           <Text style={styles.sectionTitle}>
//             Full Ranking Register
//           </Text>

//           <View style={styles.table}>
//             <View style={styles.tableHeader}>
//               <Text
//                 style={[
//                   styles.cell,
//                   styles.rankCell,
//                 ]}
//               >
//                 Rank
//               </Text>

//               <Text
//                 style={[
//                   styles.cell,
//                   styles.studentCell,
//                 ]}
//               >
//                 Student Name
//               </Text>

//               <Text
//                 style={[
//                   styles.cell,
//                   styles.classCell,
//                 ]}
//               >
//                 Grade Class
//               </Text>

//               <Text
//                 style={[
//                   styles.cell,
//                   styles.averageCell,
//                 ]}
//               >
//                 Average
//               </Text>

//               <Text
//                 style={[
//                   styles.cell,
//                   styles.gradeCell,
//                 ]}
//               >
//                 Grade
//               </Text>
//             </View>

//             {students.map((student, index) => (
//               <View
//                 key={index}
//                 style={styles.tableRow}
//               >
//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.rankCell,
//                   ]}
//                 >
//                   {student.rank}
//                 </Text>

//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.studentCell,
//                   ]}
//                 >
//                   {student.student_name}
//                 </Text>

//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.classCell,
//                   ]}
//                 >
//                   {student.grade_class}
//                 </Text>

//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.averageCell,
//                   ]}
//                 >
//                   {student.average_score}%
//                 </Text>

//                 <Text
//                   style={[
//                     styles.cell,
//                     styles.gradeCell,
//                   ]}
//                 >
//                   {student.alphabet_grade}
//                 </Text>
//               </View>
//             ))}
//           </View>
//         </View>

//         <View style={styles.section}>
//           <Text style={styles.sectionTitle}>
//             Report Summary
//           </Text>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Total Ranked Students
//             </Text>

//             <Text style={styles.summaryValue}>
//               {students.length}
//             </Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Highest Average
//             </Text>

//             <Text style={styles.summaryValue}>
//               {dux ? `${dux.average_score}%` : "--"}
//             </Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Class Dux
//             </Text>

//             <Text style={styles.summaryValue}>
//               {dux?.student_name || "--"}
//             </Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Academic Period
//             </Text>

//             <Text style={styles.summaryValue}>
//               {data.period}
//             </Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text style={styles.summaryLabel}>
//               Academic Year
//             </Text>

//             <Text style={styles.summaryValue}>
//               {data.academic_year}
//             </Text>
//           </View>
//         </View>

//         <View style={styles.signatureSection}>
//           <View style={styles.signatureBox}>
//             <View style={styles.signatureLine} />

//             <Text style={styles.signatureText}>
//               Principal
//             </Text>
//           </View>

//           <View style={styles.signatureBox}>
//             <View style={styles.signatureLine} />

//             <Text style={styles.signatureText}>
//               School Stamp
//             </Text>
//           </View>
//         </View>

//         <Text style={styles.footer}>
//           © Jana L. Williams Educational Foundation School System • Student Ranking Report
//         </Text>

//         <Text style={styles.footer}>
//           Generated by the School Management System
//         </Text>
//       </Page>
//     </Document>
//   );
// };

// export default TopStudentPerPeriodPDF;